import { useState, useEffect } from 'react';

const FloatingAIAssistant = ({ activeTab, loan, onAnalyze, lastAnalysis, onLockChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(lastAnalysis || null);

  useEffect(() => {
    if (lastAnalysis) {
      setAnalysis(lastAnalysis);
    }
  }, [lastAnalysis]);

  useEffect(() => {
    if (onLockChange) {
      onLockChange(isLocked);
    }
  }, [isLocked, onLockChange]);

  const handleLockToggle = () => {
    const newLockedState = !isLocked;
    setIsLocked(newLockedState);
    if (newLockedState) {
      setIsExpanded(true); // Auto-expand when locking
    }
  };

  const getContextualPrompt = () => {
    switch (activeTab) {
      case 'overview':
        return 'Analyze loan overview and key metrics';
      case 'borrower':
        return 'Analyze borrower profile and creditworthiness';
      case 'property':
        return 'Analyze property details and valuation';
      case 'loan':
        return 'Analyze loan structure and terms';
      case 'credit':
        return 'Analyze credit profile and debt ratios';
      case 'pricing':
        return 'Analyze pricing scenarios and rate options';
      case 'documents':
        return 'Analyze document completeness and status';
      case 'conditions':
        return 'Analyze outstanding conditions and risks';
      case 'activity':
        return 'Analyze recent activity and timeline';
      case 'messages':
        return 'Analyze communication history';
      case 'aus':
        return 'Analyze AUS findings and recommendations';
      case 'fees':
        return 'Analyze fee structure and cash to close';
      case 'scenarioPricer':
        return 'Analyze pricing scenarios and recommendations';
      default:
        return 'Analyze current loan data';
    }
  };

  const getContextualAnalysis = (tab) => {
    const baseInsights = {
      overview: {
        summary: 'Comprehensive loan analysis shows strong fundamentals with minor documentation gaps.',
        insights: [
          'Loan-to-Value ratio of 90% is within acceptable range for conventional financing',
          'Borrower credit score of 740 qualifies for best pricing tier',
          'Debt-to-Income ratio of 38% meets underwriting guidelines',
          'Recommend ordering appraisal and title work to maintain timeline'
        ],
        score: 87,
        recommendations: [
          'Order appraisal within 3 business days',
          'Request missing pay stubs from borrower',
          'Consider rate lock to protect against market volatility'
        ]
      },
      borrower: {
        summary: 'Borrower profile demonstrates strong creditworthiness and stable employment history.',
        insights: [
          'Credit score of 740 with no recent derogatory marks',
          'Employment history shows 5+ years with current employer',
          'Income documentation is 80% complete - missing recent pay stub',
          'No red flags in credit report review'
        ],
        score: 85,
        recommendations: [
          'Request most recent pay stub to complete income verification',
          'Consider ordering VOIE for faster employment verification',
          'Verify all credit inquiries with borrower'
        ]
      },
      credit: {
        summary: 'Credit profile is strong with manageable debt obligations.',
        insights: [
          'Total monthly debt obligations: $2,850',
          'Housing expense ratio: 28% (within guidelines)',
          'Back-end DTI: 38% (acceptable for conventional)',
          'No collections or charge-offs in past 24 months'
        ],
        score: 88,
        recommendations: [
          'Consider ordering VOA to verify liquid assets',
          'Review recent credit inquiries with borrower',
          'Confirm all revolving accounts are current'
        ]
      },
      property: {
        summary: 'Property details align with loan program requirements.',
        insights: [
          'Property type: Single Family Residence (eligible)',
          'Occupancy: Primary Residence (best pricing)',
          'Estimated value supports requested loan amount',
          'No appraisal ordered yet - critical path item'
        ],
        score: 82,
        recommendations: [
          'Order appraisal immediately to avoid timeline delays',
          'Verify property is not in flood zone',
          'Confirm HOA details if applicable'
        ]
      },
      documents: {
        summary: 'Document collection is progressing but requires attention.',
        insights: [
          'Core documents: 75% complete',
          'Missing: Recent pay stub, bank statements (2 months)',
          'Uploaded documents appear clear and legible',
          'No suspicious modifications detected'
        ],
        score: 78,
        recommendations: [
          'Send document request to borrower for missing items',
          'Review uploaded bank statements for NSF activity',
          'Verify all documents are dated within acceptable timeframes'
        ]
      },
      fees: {
        summary: 'Fee structure is competitive with clear cash-to-close calculation.',
        insights: [
          'Total closing costs: $15,234 (3.38% of loan amount)',
          'Lender credits offset origination charges',
          'Cash to close: $56,234 (within borrower expectations)',
          'No unusual or excessive fees detected'
        ],
        score: 90,
        recommendations: [
          'Review settlement charges with borrower',
          'Confirm title company selection',
          'Lock fees if rate is locked'
        ]
      },
      scenarioPricer: {
        summary: 'Based on 740 credit score, 90.00% LTV, and Primary occupancy, 5 of 5 products are eligible.',
        insights: [
          'Credit score of 740 qualifies for best pricing tier across all products',
          'LTV of 90% is within acceptable range for conventional financing',
          'Primary occupancy provides best rate pricing',
          '7/1 ARM offers lowest interest rate at 5.875% with competitive APR'
        ],
        score: 92,
        recommendations: [
          'Reduce LTV to 80% - Additional $50,000.00 down payment would eliminate PMI (Impact: Save $206.25/month in PMI, Effort: High)',
          'Consider 15-Year Term - 15-year term saves $361,722.60 in total interest but increases payment by $909.12/month',
          'Evaluate if borrower can afford higher payment for significant savings'
        ]
      }
    };

    return baseInsights[tab] || baseInsights.overview;
  };

  const handleAnalyze = async () => {
    setIsAnalyzing(true);
    
    // Simulate AI analysis with contextual results
    setTimeout(() => {
      const contextualAnalysis = getContextualAnalysis(activeTab);
      const mockAnalysis = {
        ...contextualAnalysis,
        timestamp: new Date().toISOString(),
        tab: activeTab
      };
      
      setAnalysis(mockAnalysis);
      setIsAnalyzing(false);
      
      if (onAnalyze) {
        onAnalyze(mockAnalysis);
      }
    }, 2000);
  };

  return (
    <div
      className="floating-ai-assistant"
      style={{
        position: 'fixed',
        right: isExpanded ? '0' : '-360px',
        top: '80px',
        width: '400px',
        height: 'calc(100vh - 100px)',
        backgroundColor: 'white',
        boxShadow: '-2px 0 10px rgba(0,0,0,0.1)',
        transition: 'right 0.3s ease',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        borderLeft: isLocked ? '3px solid var(--tblr-primary)' : '1px solid #e9ecef'
      }}
    >
      {/* Toggle Button */}
      {!isLocked && (
        <button
          className="btn btn-primary"
          onClick={() => setIsExpanded(!isExpanded)}
          style={{
            position: 'absolute',
            left: '-40px',
            top: '20px',
            width: '40px',
            height: '40px',
            borderRadius: '8px 0 0 8px',
            padding: '0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
            boxShadow: '-2px 2px 5px rgba(0,0,0,0.1)'
          }}
        >
          <i className={`ti ti-${isExpanded ? 'x' : 'robot'}`}></i>
        </button>
      )}

      {/* Panel Content */}
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
        {/* Header */}
        <div className="p-3" style={{ borderBottom: '1px solid #e9ecef', backgroundColor: '#f8f9fa' }}>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h4 className="mb-1">
                <i className="ti ti-sparkles me-2 text-primary"></i>
                AI Assistant
              </h4>
              <div className="text-secondary small">
                Context: {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}
              </div>
            </div>
            <button
              className={`btn btn-sm ${isLocked ? 'btn-primary' : 'btn-ghost-secondary'}`}
              onClick={handleLockToggle}
              title={isLocked ? 'Unlock panel' : 'Lock panel'}
            >
              <i className={`ti ti-${isLocked ? 'lock' : 'lock-open'}`}></i>
            </button>
          </div>
        </div>

        {/* Analysis Trigger */}
        <div className="p-3" style={{ borderBottom: '1px solid #e9ecef' }}>
          <button
            className="btn btn-primary w-100"
            onClick={handleAnalyze}
            disabled={isAnalyzing}
          >
            {isAnalyzing ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Analyzing...
              </>
            ) : (
              <>
                <i className="ti ti-wand me-2"></i>
                {getContextualPrompt()}
              </>
            )}
          </button>
        </div>

        {/* Analysis Results */}
        <div className="p-3" style={{ flex: 1, overflowY: 'auto' }}>
          {analysis ? (
            <div>
              <div className="alert alert-success mb-3">
                <div className="d-flex align-items-center mb-2">
                  <div className="h1 mb-0 me-3">{analysis.score}</div>
                  <div>
                    <strong>Analysis Score</strong>
                    <div className="text-secondary small">Based on current data</div>
                  </div>
                </div>
              </div>

              <h5 className="mb-2">Summary</h5>
              <p className="text-secondary mb-3">{analysis.summary}</p>

              <h5 className="mb-2">Key Insights</h5>
              <ul className="mb-3">
                {analysis.insights.map((insight, idx) => (
                  <li key={idx} className="mb-2">{insight}</li>
                ))}
              </ul>

              {analysis.recommendations && analysis.recommendations.length > 0 && (
                <>
                  <h5 className="mb-2">
                    <i className="ti ti-bulb me-1 text-warning"></i>
                    Recommendations
                  </h5>
                  <ul className="mb-0">
                    {analysis.recommendations.map((rec, idx) => (
                      <li key={idx} className="mb-2 text-primary">{rec}</li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          ) : (
            <div className="text-center text-secondary py-5">
              <i className="ti ti-bulb" style={{ fontSize: '3rem', opacity: 0.3 }}></i>
              <div className="mt-2">Click "Analyze" to get AI insights for this tab</div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3" style={{ borderTop: '1px solid #e9ecef', backgroundColor: '#f8f9fa' }}>
          <div className="text-secondary small text-center">
            <i className="ti ti-info-circle me-1"></i>
            AI analysis is contextual to your current view
          </div>
        </div>
      </div>
    </div>
  );
};

export default FloatingAIAssistant;
